<script setup>
import { isVNode, computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import Policy from 'dashboard/components/policy.vue';
import { useSidebarContext } from './provider';
import SidebarUnreadBadge from './SidebarUnreadBadge.vue';

const props = defineProps({
  label: { type: String, required: true },
  to: { type: [String, Object], required: true },
  icon: { type: [String, Object], default: null },
  active: { type: Boolean, default: false },
  component: { type: Function, default: null },
  badgeCount: { type: [Number, String], default: 0 },
});

const { resolvePermissions, resolveFeatureFlag } = useSidebarContext();

const shouldRenderComponent = computed(() => {
  return typeof props.component === 'function' || isVNode(props.component);
});
</script>

<template>
  <Policy
    :permissions="resolvePermissions(to)"
    :feature-flag="resolveFeatureFlag(to)"
    as="li"
    class="py-px ps-2 ms-3 relative text-n-slate-11 min-w-0"
  >
    <component
      :is="to ? 'router-link' : 'div'"
      :to="to"
      :title="label"
      class="flex h-7 items-center gap-2 px-2.5 py-1 rounded-full hover:bg-n-alpha-2 group min-w-0"
      :class="{
        'text-n-solid-1 bg-n-slate-12 hover:bg-n-slate-12 [&_.sidebar-icon]:text-n-blue-8 dark:[&_.sidebar-icon]:text-n-blue-9 active':
          active,
      }"
    >
      <component
        :is="component"
        v-if="shouldRenderComponent"
        v-bind="{ label, icon, active, badgeCount }"
      />
      <template v-else>
        <span v-if="icon" class="size-4 grid place-content-center rounded-full">
          <Icon :icon="icon" class="sidebar-icon size-4 inline-block" />
        </span>
        <div class="flex-1 truncate min-w-0 text-[13px]">{{ label }}</div>
        <SidebarUnreadBadge :count="badgeCount" :active="active" />
      </template>
    </component>
  </Policy>
</template>
